import React, { useEffect, useState } from "react";
import { api, fmtDate } from "../../lib/api";
import { ArrowUpRight } from "lucide-react";
import { Kpi, Th, Td, Pill, SectionTitle, Loading, STATUS_STYLE, usd, num } from "./shared";

export const KubeSavings = ({ clusterId }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.kube.savings(clusterId).then(setData).catch(console.error);
  }, [clusterId]);

  if (!data) return <Loading label="Loading realized savings…" testId="kube-savings-loading" />;

  return (
    <div className="space-y-6" data-testid="kube-savings">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <Kpi testId="kube-sav-realized" label="Realized / mo" value={usd(data.realized_usd)} sub={`${data.workloads_optimized} workloads · last ${data.window_days} days`} accent="text-[#15803d]" />
        <Kpi testId="kube-sav-potential" label="Potential / mo" value={usd(data.potential_usd)} sub="if every workload were attached" />
        <Kpi testId="kube-sav-rate" label="Realization rate" value={`${data.realization_rate_pct}%`} sub="realized ÷ potential" accent="text-[#00579e]" />
        <Kpi testId="kube-sav-reliability" label="Reliability spend" value={usd(data.reliability_spend_usd)} sub="cost of scale-ups that stopped OOM kills" accent="text-[#92400e]" />
        <Kpi testId="kube-sav-gap" label="Unrealized" value={usd(Math.max(0, data.potential_usd - data.realized_usd))} sub="close it by attaching rules" accent="text-[#be185d]" />
      </div>

      <div className="cs-card p-5" data-testid="kube-realization-bar">
        <SectionTitle hint={data.counterfactual_note}>Realization funnel</SectionTitle>
        <div className="space-y-2">
          {[["Potential", data.potential_usd, "#cbd5e1"], ["Realized", data.realized_usd, "#15803d"]].map(([l, v, color]) => (
            <div key={l} className="flex items-center gap-3">
              <span className="w-20 text-xs text-muted-foreground">{l}</span>
              <div className="flex-1 h-5 rounded-sm bg-[hsl(var(--cs-tint))] overflow-hidden">
                <div className="h-full rounded-sm flex items-center justify-end pr-2 transition-all duration-500"
                  style={{ width: `${Math.max(6, (v / data.potential_usd) * 100)}%`, background: color }}>
                  <span className="cs-mono text-[10px] text-white font-semibold">{usd(v)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {data.by_namespace.map((n) => (
            <span key={n.namespace} className="px-2.5 py-1 rounded-sm border border-border text-[11px]" data-testid={`kube-sav-ns-${n.namespace}`}>
              <span className="font-medium">{n.namespace}</span>
              <span className="cs-mono text-muted-foreground ml-1.5">{usd(n.savings_usd)} · {n.share_pct}%</span>
            </span>
          ))}
        </div>
      </div>

      <div className="cs-card p-5" data-testid="kube-savings-table">
        <SectionTitle hint="&quot;Without ASTRA&quot; is the pre-change request priced at the same billing rates for the same period.">
          Per-workload savings · with vs without ASTRA
        </SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr><Th>Workload</Th><Th>Namespace</Th><Th>Status</Th><Th right>vCPU with</Th><Th right>vCPU without</Th><Th right>Mem with</Th><Th right>Mem without</Th><Th right>Savings / mo</Th><Th>Optimized</Th></tr>
            </thead>
            <tbody>
              {data.workloads.map((w) => (
                <tr key={w.workload} className="border-t border-border" data-testid={`kube-sav-row-${w.workload}`}>
                  <Td className="font-medium">
                    {w.workload}
                    {w.reliability_spend && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-[#92400e]" title={w.note}>
                        <ArrowUpRight className="w-3 h-3" /> scaled up
                      </span>
                    )}
                  </Td>
                  <Td className="text-muted-foreground">{w.namespace}</Td>
                  <Td><Pill cls={STATUS_STYLE[w.status].cls}>{STATUS_STYLE[w.status].label}</Pill></Td>
                  <Td right mono className="text-[#15803d]">{num(w.cpu_with_astra_cores, 2)}</Td>
                  <Td right mono className="text-muted-foreground">{num(w.cpu_without_astra_cores, 2)}</Td>
                  <Td right mono className="text-[#15803d]">{num(w.mem_with_astra_gib, 1)} GiB</Td>
                  <Td right mono className="text-muted-foreground">{num(w.mem_without_astra_gib, 1)} GiB</Td>
                  <Td right mono className={`font-semibold ${w.savings_usd < 0 ? "text-[#92400e]" : ""}`}>{usd(w.savings_usd)}</Td>
                  <Td mono className="text-muted-foreground">{fmtDate(w.optimized_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!data.workloads.length && (
          <div className="text-center text-sm text-muted-foreground py-8" data-testid="kube-savings-empty">
            No workloads are attached to a rule on this cluster yet — attach one in Automation &amp; Rules to start realizing savings.
          </div>
        )}
      </div>
    </div>
  );
};

export default KubeSavings;
